import { normalizeMoodIconKey, type MoodIconKey } from '@nexacore/config/moodIconKeys';

type MoodIconProps = {
  iconKey?: string;
};

const iconProps = {
  viewBox: '0 0 48 48',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  focusable: 'false',
  'aria-hidden': true,
} as const;

function PositiveIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="24" cy="24" r="13" stroke="currentColor" strokeWidth="2.8" />
      <path
        d="M17.5 26.5c2.7 4.3 10.3 4.3 13 0"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinecap="round"
      />
      <path
        d="M24 4v5M24 39v5M4 24h5M39 24h5M9.9 9.9l3.6 3.6M34.5 34.5l3.6 3.6M38.1 9.9l-3.6 3.6M13.5 34.5l-3.6 3.6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <circle cx="19" cy="21" r="1.7" fill="currentColor" />
      <circle cx="29" cy="21" r="1.7" fill="currentColor" />
    </svg>
  );
}

function TiredIcon() {
  return (
    <svg {...iconProps}>
      <path
        d="M31.5 7.5A16.5 16.5 0 1 0 41 35.2 14.3 14.3 0 0 1 31.5 7.5Z"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinejoin="round"
      />
      <path
        d="M14 23c2-2 5-2 7 0M27 23c2-2 5-2 7 0M18 32c3.3-2.4 8.7-2.4 12 0"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LowMoodIcon() {
  return (
    <svg {...iconProps}>
      <rect x="9" y="16" width="27" height="16" rx="4" stroke="currentColor" strokeWidth="2.8" />
      <path d="M39 21v6M14 24h5" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
      <path d="M28 11 23 24h6l-5 13" stroke="currentColor" strokeWidth="2.4" />
    </svg>
  );
}

function HealingIcon() {
  return (
    <svg {...iconProps}>
      <path d="M24 39V22" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
      <path
        d="M24 25c-8.4-1-12.6-6.2-13.4-15.4C19.6 10.2 24 15 24 25ZM24 28c9-1 13.5-6.6 14.2-16.4C28.6 12.4 24 17.4 24 28Z"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinejoin="round"
      />
      <path d="M16 39h16" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
    </svg>
  );
}

function FocusIcon() {
  return (
    <svg {...iconProps}>
      <rect x="12" y="13" width="24" height="17" rx="3" stroke="currentColor" strokeWidth="2.8" />
      <path
        d="M8 36h32M20 30l-2 6M28 30l2 6M24 20v4M18 22h12"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinecap="round"
      />
      <circle cx="24" cy="22" r="9" stroke="currentColor" strokeWidth="2" opacity=".35" />
    </svg>
  );
}

function CelebrateIcon() {
  return (
    <svg {...iconProps}>
      <path
        d="m14 34 5-18 13 13-18 5Z"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinejoin="round"
      />
      <path
        d="M28 9v5M37 14l-4 4M39 25h-5M19 16l13 13M16 8l1.5 3L21 12.5 17.5 14 16 17l-1.5-3L11 12.5l3.5-1.5L16 8Z"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const iconByKey: Record<MoodIconKey, () => JSX.Element> = {
  positive: PositiveIcon,
  tired: TiredIcon,
  low_mood: LowMoodIcon,
  healing: HealingIcon,
  focus: FocusIcon,
  celebrate: CelebrateIcon,
};

export function MoodIcon({ iconKey }: MoodIconProps) {
  const normalizedIconKey = normalizeMoodIconKey(iconKey);
  const Icon = iconByKey[normalizedIconKey];
  return (
    <span className="mood-icon" data-mood-icon-key={normalizedIconKey}>
      <Icon />
    </span>
  );
}
